import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { TbChevronDown, TbSearch } from "react-icons/tb";
import {
  PORTAL_DROPDOWN_BACKDROP_Z_INDEX,
  PORTAL_DROPDOWN_Z_INDEX,
  usePortalDropdownPosition,
} from "../../../../Hooks/usePortalDropdownPosition";

const TRIGGER_CLASS =
  "valut-input flex h-11 w-full min-w-0 items-center justify-between gap-2 rounded-xl !pr-10 text-left text-sm";

const SEARCH_THRESHOLD = 6;

export const CrossborderSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Selecione",
  id,
  className = "",
  required = false,
  formatOption,
  getSelected,
  getOptionSearchText,
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nenhuma opção encontrada.",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { triggerRef, menuRef, menuStyle } = usePortalDropdownPosition(open);

  const selected = getSelected ? getSelected(value) : options.find((o) => o.id === value);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => {
      const searchText = getOptionSearchText
        ? getOptionSearchText(option)
        : [
            option.label,
            option.id,
            option.description,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

      return searchText.includes(term);
    });
  }, [options, search, getOptionSearchText]);

  const showSearch = options.length >= SEARCH_THRESHOLD;

  const handleSelect = (optionId) => {
    onChange(optionId);
    setOpen(false);
    setSearch("");
  };

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  const defaultFormat = (option) => (
    <span className="min-w-0 truncate font-semibold text-brand-dark">{option.label}</span>
  );

  const renderOption = formatOption ?? defaultFormat;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="mb-1.5 block text-xs font-medium text-gray-500">
          {label}
          {required && <span className="text-[#e7a73f]"> *</span>}
        </label>
      )}

      <div className="relative">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className={`${TRIGGER_CLASS} ${selected ? "text-brand-dark" : "text-gray-400"}`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {selected ? renderOption(selected) : placeholder}
          </span>
        </button>
        <TbChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />

        {open &&
          menuStyle &&
          createPortal(
            <>
              <div
                className="fixed inset-0"
                style={{ zIndex: PORTAL_DROPDOWN_BACKDROP_Z_INDEX }}
                onClick={handleClose}
                aria-hidden="true"
              />
              <div
                ref={menuRef}
                role="listbox"
                style={{
                  ...menuStyle,
                  zIndex: PORTAL_DROPDOWN_Z_INDEX,
                  display: "flex",
                  flexDirection: "column",
                }}
                className="app-card overflow-hidden"
              >
                {showSearch && (
                  <div className="shrink-0 border-b border-gray-100 p-2">
                    <div className="relative">
                      <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="valut-input h-9 w-full rounded-lg pl-9 pr-3 text-sm"
                        autoFocus
                      />
                    </div>
                  </div>
                )}

                <div className="min-h-0 flex-1 overflow-y-auto py-1">
                  {filteredOptions.length === 0 ? (
                    <p className="px-3 py-4 text-center text-xs text-gray-400">{emptyMessage}</p>
                  ) : (
                    filteredOptions.map((option) => {
                      const isActive = option.id === value;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          onClick={() => handleSelect(option.id)}
                          className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                            isActive
                              ? "bg-[#fff8ec] text-brand-dark dark:bg-brand-dark-elevated dark:text-gray-100"
                              : "text-brand-dark hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-[#1f1f1f]"
                          }`}
                        >
                          {renderOption(option)}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </>,
            document.body
          )}
      </div>
    </div>
  );
};
